export const TRIGGER_TYPE_OPTIONS = Object.freeze([
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'bot', label: 'Bot' },
  { value: 'moveToCampaign', label: 'Move to campaign' },
  { value: 'assignLead', label: 'Assign lead' },
]);

export function getTriggerTypeLabel(type) {
  return TRIGGER_TYPE_OPTIONS.find((item) => item.value === type)?.label || type || 'Trigger';
}

const KNOWN_TRIGGER_TYPES = new Set(TRIGGER_TYPE_OPTIONS.map((item) => item.value));

export function isKnownTriggerType(type) {
  return KNOWN_TRIGGER_TYPES.has(type);
}

export function normalizeProfileUserMap(profileUserMap = {}) {
  const normalized = {};
  Object.entries(profileUserMap || {}).forEach(([key, users]) => {
    normalized[String(key)] = (Array.isArray(users) ? users : []).map((user) => ({
      id: user?.id,
      username: user?.username ?? user?.name,
    }));
  });
  return normalized;
}

export function normalizeSelectedProfiles(profiles = []) {
  return (Array.isArray(profiles) ? profiles : [])
    .filter((profile) => profile?.id != null)
    .map((profile) => ({
      id: profile.id,
      name: profile.name || profile.label || '',
    }));
}

/** Rebuild UI-only fields (profiles, users map) from API-saved trigger shape. */
export function hydrateTriggerForEdit(trigger) {
  if (!trigger) return createEmptyTrigger('assignLead');

  const hydrated = { ...trigger };
  const type = isKnownTriggerType(hydrated.trigger_type)
    ? hydrated.trigger_type
    : 'assignLead';
  hydrated.trigger_type = type;

  const stakeholders = hydrated.stakeholders;
  const hasProfileStakeholders =
    Array.isArray(stakeholders)
    && stakeholders.length > 0
    && stakeholders !== 'end_customer';

  if (
    (type === 'assignLead' || type === 'whatsapp')
    && hasProfileStakeholders
  ) {
    const profilesFromStakeholders = [];
    const profileUserMapFromStakeholders = {};

    stakeholders.forEach((stakeholder) => {
      if (!stakeholder || typeof stakeholder !== 'object' || stakeholder.id == null) {
        return;
      }
      const pid = String(stakeholder.id);
      profilesFromStakeholders.push({ id: stakeholder.id, name: stakeholder.name });
      profileUserMapFromStakeholders[pid] = (stakeholder.users || []).map((user) => ({
        id: user?.id,
        username: user?.username ?? user?.name,
      }));
    });

    if (profilesFromStakeholders.length) {
      hydrated.selectedProfiles = normalizeSelectedProfiles(
        hydrated.selectedProfiles?.length
          ? hydrated.selectedProfiles
          : profilesFromStakeholders
      );
      hydrated.profileUserMap = {
        ...profileUserMapFromStakeholders,
        ...normalizeProfileUserMap(hydrated.profileUserMap),
      };
    }
  }

  if (Array.isArray(hydrated.selectedProfiles) && hydrated.selectedProfiles.length) {
    hydrated.selectedProfiles = normalizeSelectedProfiles(hydrated.selectedProfiles);
  }

  if (hydrated.profileUserMap) {
    hydrated.profileUserMap = normalizeProfileUserMap(hydrated.profileUserMap);
  }

  if (type === 'whatsapp') {
    if (stakeholders === 'end_customer') {
      hydrated.stakeholderTarget = 'endcustomer';
    } else if (hasProfileStakeholders) {
      hydrated.stakeholderTarget = 'profile';
    }
  }

  return hydrated;
}

export function createEmptyTrigger(type = 'sms') {
  const base = { trigger_type: type };
  if (type === 'whatsapp') {
    return {
      ...base,
      stakeholderTarget: 'endcustomer',
      stakeholders: 'end_customer',
      organisation: null,
      botNumber: null,
      bot_number: '',
      template: null,
      template_id: '',
      profileUserMap: {},
      selectedProfiles: [],
      users: [],
    };
  }
  if (type === 'assignLead') {
    return {
      ...base,
      organisation: null,
      profileUserMap: {},
      selectedProfiles: [],
      users: [],
      stakeholders: [],
    };
  }
  if (type === 'moveToCampaign') {
    return { ...base, childCampaign: null };
  }
  if (type === 'email') {
    return { ...base, sender_email: '', stakeholders: [] };
  }
  if (type === 'bot') {
    return { ...base, organisation: null, botNumber: null, bot_number: '', flow_id: '' };
  }
  return { ...base, sender_id: '', dlt_id: '', stakeholders: [] };
}

export function summarizeTrigger(trigger) {
  if (!trigger?.trigger_type) return 'Trigger';

  const typeLabel = getTriggerTypeLabel(trigger.trigger_type);

  if (trigger.trigger_type === 'whatsapp') {
    const templateName = trigger.template?.name || trigger.template_id || '';
    const recipient =
      trigger.stakeholders === 'end_customer' || trigger.stakeholderTarget === 'endcustomer'
        ? 'End customer'
        : 'Profile';
    return [typeLabel, templateName, recipient].filter(Boolean).join(' · ');
  }

  if (trigger.trigger_type === 'email') {
    return [typeLabel, trigger.sender_email].filter(Boolean).join(' · ');
  }

  if (trigger.trigger_type === 'sms') {
    return [typeLabel, trigger.sender_id].filter(Boolean).join(' · ');
  }

  if (trigger.trigger_type === 'bot') {
    return [typeLabel, trigger.flow_id || trigger.bot_number].filter(Boolean).join(' · ');
  }

  if (trigger.trigger_type === 'moveToCampaign') {
    return [typeLabel, trigger.childCampaign?.child_campaign_name].filter(Boolean).join(' · ');
  }

  if (trigger.trigger_type === 'assignLead') {
    const profileCount =
      (trigger.selectedProfiles || []).length
      || (Array.isArray(trigger.stakeholders) ? trigger.stakeholders.length : 0);
    return profileCount ? `${typeLabel} · ${profileCount} profile(s)` : typeLabel;
  }

  return typeLabel;
}

export function validateTriggerDraft(trigger) {
  const errors = {};
  if (!trigger?.trigger_type) {
    errors.trigger_type = 'Trigger type is required';
    return errors;
  }

  switch (trigger.trigger_type) {
    case 'email':
      if (!String(trigger.sender_email || '').trim()) {
        errors.sender_email = 'Sender email is required';
      }
      break;
    case 'sms':
      if (!String(trigger.sender_id || '').trim()) {
        errors.sender_id = 'Sender ID is required';
      }
      break;
    case 'whatsapp':
      if (!trigger.organisation?.organisation_id) {
        errors.organisation = 'Organisation is required';
      }
      if (!trigger.bot_number && !trigger.botNumber?.wa_phone_number) {
        errors.bot_number = 'Bot number is required';
      }
      if (!trigger.template_id && !trigger.template?.wa_Id) {
        errors.template = 'Template is required';
      }
      break;
    case 'bot':
      if (!trigger.organisation?.organisation_id) {
        errors.organisation = 'Organisation is required';
      }
      if (!String(trigger.flow_id || '').trim()) {
        errors.flow_id = 'Flow ID is required';
      }
      break;
    case 'moveToCampaign':
      if (!trigger.childCampaign?.child_campaign_id) {
        errors.childCampaign = 'Child campaign is required';
      }
      break;
    case 'assignLead': {
      const hasProfiles = (trigger.selectedProfiles || []).length > 0;
      const hasStakeholderProfiles =
        Array.isArray(trigger.stakeholders) && trigger.stakeholders.length > 0;
      const hasUsers = (trigger.users || []).length > 0;
      if (!hasProfiles && !hasStakeholderProfiles && !hasUsers) {
        errors.users = 'Select at least one profile or user';
      }
      break;
    }
    default:
      break;
  }

  return errors;
}

/** Build stakeholders array for save from whatsapp profile selection. */
export function buildTriggerStakeholdersFromDraft(trigger) {
  if (trigger.stakeholders === 'end_customer' || trigger.stakeholderTarget === 'endcustomer') {
    return 'end_customer';
  }

  const profileUserMap = trigger.profileUserMap || {};
  const selectedProfiles = trigger.selectedProfiles || [];

  if (selectedProfiles.length) {
    return selectedProfiles.map((profile) => ({
      id: profile.id,
      name: profile.name,
      users: profileUserMap[String(profile.id)] || [],
    }));
  }

  return Array.isArray(trigger.stakeholders) ? trigger.stakeholders : [];
}

function pickTriggerFieldsForSave(trigger) {
  const type = trigger?.trigger_type;
  const stakeholders = buildTriggerStakeholdersFromDraft(trigger);

  if (type === 'assignLead') {
    return {
      trigger_type: 'assignLead',
      organisation: trigger.organisation ?? null,
      selectedProfiles: trigger.selectedProfiles ?? [],
      profileUserMap: trigger.profileUserMap ?? {},
      users: trigger.users ?? [],
      stakeholders,
    };
  }

  if (type === 'whatsapp') {
    return {
      ...trigger,
      stakeholders,
      template: trigger.template ?? null,
      template_id: trigger.template_id ?? '',
    };
  }

  if (type === 'bot') {
    return {
      trigger_type: 'bot',
      organisation: trigger.organisation ?? null,
      botNumber: trigger.botNumber ?? null,
      bot_number: trigger.bot_number ?? '',
      flow_id: trigger.flow_id ?? '',
    };
  }

  if (type === 'email') {
    return {
      trigger_type: 'email',
      sender_email: trigger.sender_email ?? '',
      stakeholders,
    };
  }

  if (type === 'sms') {
    return {
      trigger_type: 'sms',
      sender_id: trigger.sender_id ?? '',
      dlt_id: trigger.dlt_id ?? '',
      stakeholders,
    };
  }

  if (type === 'moveToCampaign') {
    return {
      trigger_type: 'moveToCampaign',
      childCampaign: trigger.childCampaign ?? null,
    };
  }

  return { ...trigger, stakeholders };
}

export function normalizeTriggerForSave(trigger) {
  return pickTriggerFieldsForSave(trigger);
}
